import React, {useState} from 'react';
import {Platform, ScrollView, StyleSheet, View} from 'react-native';
import Animated, {FadeIn, FadeOut} from 'react-native-reanimated';
import {useHeaderHeight} from '@react-navigation/elements';
import {observer} from 'mobx-react-lite';

import CouponModal from '../../../components/modals/CouponModal';
import type {
  ApplePayIOSConfig,
  ApplePayWebConfig,
  GooglePayWebConfig,
  PaymentMethodType,
} from '../../../components/payments/till/PaymentMethodSelector/types';
import TitleLine from '../../../components/rewards/TitleLine';
import Button from '../../../components/themed/Button';
import ContentView from '../../../components/ui-controls/layout/ContentView';
import {AppRoute} from '../../../models/AppRoute';
import {
  PAYMENT_SERVICE_TYPES,
  usePaymentCard,
  usePaymentServices,
} from '../../../queries/paymentCard';
import {useStores} from '../../../stores/use-stores';
import CartCoupon from '../CartCoupon';
import {CheckoutActionButton} from '../components/CheckoutActionButton';
import ConfirmOrderPlacementModal from '../components/ConfirmOrderPlacementModal';
import CouponBlockedNotice from '../components/CouponBlockedNotice';
import OrderTotals from '../components/OrderTotals';
import PickupDeliveryDetails from '../components/PickupDeliveryDetails';
import {
  createApplePayCheckoutConfigs,
  createGooglePayCheckoutConfigs,
  useCheckoutSession,
  useCheckoutStepValidation,
  useOrderSubmission,
  usePaymentValidation,
} from '../hooks';
import {handleContinue} from '../util/validation';

import CartContent from './content/CartContent';
import CheckoutContent from './content/CheckoutContent';
import PaymentContent from './content/PaymentContent';
import ReviewContent from './content/ReviewContent';
import type {
  CheckoutNavigation,
  CheckoutStep,
  StepContentProps,
  UnifiedCheckoutScreenProps,
} from './types';
import {CHECKOUT_STEPS, getStepConfig} from './types';

/**
 * Render left side content based on step
 */
function StepContent({
  step,
  navigation,
  selectedPaymentMethod,
  onPaymentMethodChange,
  applePayConfig,
  onCanPayChange,
}: StepContentProps & {step: CheckoutStep}) {
  switch (step) {
    case 'cart':
      return <CartContent navigation={navigation} />;
    case 'checkout':
      return <CheckoutContent navigation={navigation} />;
    case 'review':
      return <ReviewContent navigation={navigation} />;
    case 'payment':
      return (
        <PaymentContent
          navigation={navigation}
          selectedPaymentMethod={selectedPaymentMethod}
          onPaymentMethodChange={onPaymentMethodChange}
          applePayConfig={applePayConfig}
          onCanPayChange={onCanPayChange}
        />
      );
    default:
      return <CartContent navigation={navigation} />;
  }
}

/**
 * Get action button text for current step
 */
function getActionButtonText(step: CheckoutStep): string {
  const stepConfig = getStepConfig(step);
  return stepConfig?.actionLabel || 'CONTINUE';
}

/**
 * Step-specific action button
 * Handles cart login check, payment confirmation, etc.
 * For payment step, uses CheckoutActionButton which shows Apple Pay button when selected.
 */
const StepActionButton = observer(
  ({
    step,
    navigation,
    onStepComplete,
    onPaymentPress,
    selectedPaymentMethod,
    applePayConfig,
    applePayIOSConfig,
    googlePayWebConfig,
    canPay = true,
    canProceedToReview,
  }: {
    step: CheckoutStep;
    navigation: CheckoutNavigation;
    onStepComplete: () => void;
    onPaymentPress: () => void;
    selectedPaymentMethod?: PaymentMethodType;
    applePayConfig?: ApplePayWebConfig;
    applePayIOSConfig?: ApplePayIOSConfig;
    googlePayWebConfig?: GooglePayWebConfig;
    canPay?: boolean;
    canProceedToReview?: boolean;
  }) => {
    const {cartStore, userStore} = useStores();

    // DIS-643: Cart step skips ALL validation (server-side validateOrder fails without time slot)
    // Just do login check + navigation directly, same as mobile CartScreen
    const handleCartPress = () => {
      if (!userStore.isLoggedIn) {
        userStore.setPostLoginReturnToScreen(AppRoute.CART);
        navigation.navigate(AppRoute.ACCOUNT_NOT_LOGGED_IN);
        return;
      }
      onStepComplete();
    };

    // Wrap step transitions with client-side validation only (checkout, review steps)
    // Skip server-side validateOrder() - only do server validation on payment step
    const handlePress = handleContinue({
      callback: () => {
        // Payment step: Validate and show confirmation modal
        if (step === 'payment') {
          onPaymentPress();
          return;
        }

        // Other steps: Navigate to next step
        onStepComplete();
      },
      cartStore,
      userStore,
      skipServerValidation: true,
    });

    // Payment step: Use CheckoutActionButton for Apple Pay support
    if (step === 'payment') {
      const isLoadingOrSubmitting = cartStore.submittingOrder || cartStore.isLoadingOrder;
      // DIS-643: Server validation happens here BEFORE showing confirmation modal
      const validatedPaymentPress = handleContinue({
        callback: onPaymentPress,
        cartStore,
        userStore,
      });
      return (
        <>
          <CheckoutActionButton
            selectedPaymentMethod={selectedPaymentMethod ?? 'card'}
            applePayConfig={applePayConfig}
            applePayIOSConfig={applePayIOSConfig}
            googlePayWebConfig={googlePayWebConfig}
            onPayNow={validatedPaymentPress}
            isLoading={isLoadingOrSubmitting}
            isDisabled={isLoadingOrSubmitting || !canPay || cartStore.couponBlocksCheckout}
            testID="unified-checkout-action-button"
          />
          <CouponBlockedNotice />
        </>
      );
    }

    // Other steps: Standard button
    const isCartEmpty = step === 'cart' && cartStore.getTotalOrderCount() === 0;
    const isCheckoutStepInvalid = step === 'checkout' && canProceedToReview === false;
    const isDisabled =
      cartStore.submittingOrder ||
      cartStore.isLoadingOrder ||
      isCartEmpty ||
      isCheckoutStepInvalid ||
      cartStore.couponBlocksCheckout;

    return (
      <>
        <Button
          title={
            cartStore.couponBlocksCheckout
              ? 'ADD MORE TO CONTINUE WITH COUPON'
              : getActionButtonText(step)
          }
          onPress={step === 'cart' ? handleCartPress : handlePress}
          disabled={isDisabled}
          loading={cartStore.submittingOrder || cartStore.isLoadingOrder}
          testID="unified-checkout-action-button"
        />
        <CouponBlockedNotice />
      </>
    );
  },
);

/**
 * Unified checkout layout for web desktop
 *
 * Layout: 2/3 left (step content) + 1/3 right (totals + action button)
 */
const UnifiedCheckoutScreen = observer(({step, navigation}: UnifiedCheckoutScreenProps) => {
  const {cartStore, userStore} = useStores();
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<PaymentMethodType>('card');
  const [canPay, setCanPay] = useState(true); // Whether payment can proceed (false when card selected but no card saved)
  const [isCouponModalVisible, setIsCouponModalVisible] = useState(false);

  // DIS-1127: When this screen is rendered for the cart step, the parent
  // Cart route has headerTransparent: true (the BrandHeader is absolute-
  // positioned). We must compensate with paddingTop on the scroll content
  // so the first item isn't hidden under the header. For checkout/review/
  // payment steps, the parent route's header is in normal flex flow and
  // no compensation is needed (adding paddingTop would create a double
  // offset).
  const headerHeight = useHeaderHeight();
  const cartHeaderPaddingTop = step === 'cart' ? headerHeight : 0;

  // Session management: login reload + empty cart redirect
  // Cart step shows EmptyCart component instead of redirecting
  useCheckoutSession({
    step,
    skipEmptyCartRedirect: step === 'cart',
  });

  // Get payment card for validation
  const {data: paymentCardData} = usePaymentCard();
  const paymentCard = paymentCardData?.card;

  // Get Apple Pay / Google Pay service IDs from payment services
  const {data: paymentServicesData} = usePaymentServices();
  const applePayServiceId =
    paymentServicesData?.services.find(s => s.type === PAYMENT_SERVICE_TYPES.APPLE_PAY)?.id ?? null;
  const googlePayServiceId =
    paymentServicesData?.services.find(s => s.type === PAYMENT_SERVICE_TYPES.GOOGLE_PAY)?.id ??
    null;

  // Apple Pay configs
  const {applePayConfig, applePayIOSConfig} = createApplePayCheckoutConfigs({
    cartStore,
    applePayServiceId,
    logTag: 'UnifiedCheckoutScreen',
  });

  // Google Pay web config (desktop web checkout)
  const {googlePayWebConfig} = createGooglePayCheckoutConfigs({
    cartStore,
    googlePayServiceId,
    logTag: 'UnifiedCheckoutScreen',
  });

  // Payment validation with billingAddress check
  const {validateWithToast} = usePaymentValidation({
    cartStore,
    userStore,
    paymentCard,
    selectedPaymentMethod,
  });

  // Checkout step validation (for REVIEW ORDER button)
  const {canProceedToReview} = useCheckoutStepValidation({cartStore, userStore});

  // Order submission - consolidated logic for validation, modal, and API call
  const {handlePlaceOrder, submitOrder, isConfirmModalVisible, closeConfirmModal} =
    useOrderSubmission({
      cartStore,
      // Web doesn't need browserData passed - hook retrieves it directly
      paymentCardId: paymentCard?.id,
      validateOrder: validateWithToast,
    });

  const handleStepComplete = () => {
    const nextStepConfig = CHECKOUT_STEPS.find((_s, i) => CHECKOUT_STEPS[i - 1]?.key === step);
    if (nextStepConfig) {
      if (nextStepConfig.route === AppRoute.CART_ORDER_CHECKOUT) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (navigation as any).navigate(AppRoute.ROOT_CHECKOUT_FLOW, {
          screen: nextStepConfig.route,
        });
      } else {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (navigation as any).navigate(nextStepConfig.route);
      }
    }
  };

  const stepConfig = getStepConfig(step);

  return (
    <ContentView testID="unified-checkout-screen" style={{paddingTop: cartHeaderPaddingTop}}>
      {/* Header - spans full width above the two-column layout */}
      <TitleLine title={stepConfig?.title} />

      <View style={styles.container}>
        {/* Left - Step content */}
        <ScrollView
          style={styles.main}
          contentContainerStyle={styles.mainContent}
          showsVerticalScrollIndicator={true}>
          <Animated.View
            key={`content-${step}`}
            entering={FadeIn.duration(200)}
            exiting={FadeOut.duration(100)}>
            <StepContent
              step={step}
              navigation={navigation}
              selectedPaymentMethod={selectedPaymentMethod}
              onPaymentMethodChange={setSelectedPaymentMethod}
              applePayConfig={applePayConfig}
              onCanPayChange={setCanPay}
            />
          </Animated.View>
        </ScrollView>

        {/* Right Sidebar */}
        <View style={styles.sidebar}>
          {/* Pickup/Delivery - has its own Card styling */}
          {(step === 'review' || step === 'payment') && <PickupDeliveryDetails />}

          {/* Coupon - standalone link */}
          {/*
            DIS-1675: on the cart step the row also renders at zero count when a coupon is
            applied, so emptying the cart does not strip the only way to change or clear it.
            The checkout and review steps keep the count-only gate.
          */}
          {(step === 'cart' || step === 'checkout' || step === 'review') &&
            (cartStore.getTotalOrderCount() > 0 ||
              (step === 'cart' && !!cartStore.currentOrder?.coupon_code)) && (
              <CartCoupon onCouponPress={() => setIsCouponModalVisible(true)} />
            )}

          {/* Order Summary Card */}
          <View style={styles.card}>
            <OrderTotals noBorder>
              <StepActionButton
                step={step}
                navigation={navigation}
                onStepComplete={handleStepComplete}
                onPaymentPress={handlePlaceOrder}
                selectedPaymentMethod={selectedPaymentMethod}
                applePayConfig={applePayConfig}
                applePayIOSConfig={applePayIOSConfig}
                googlePayWebConfig={googlePayWebConfig}
                canPay={canPay}
                canProceedToReview={canProceedToReview}
              />
            </OrderTotals>
          </View>
        </View>
      </View>

      <ConfirmOrderPlacementModal
        isVisible={isConfirmModalVisible}
        onClose={closeConfirmModal}
        onSubmit={submitOrder}
        isSubmitting={cartStore.submittingOrder}
        includeToast={false}
      />

      <CouponModal open={isCouponModalVisible} callback={() => setIsCouponModalVisible(false)} />
    </ContentView>
  );
});

export default UnifiedCheckoutScreen;

const styles = StyleSheet.create({
  container: {
    flex: 1,
    flexDirection: 'row',
    gap: 24,
  },
  main: {
    // minWidth 0 so a wide child (a long product name, a table) cannot push the
    // flex item past its basis and squeeze the sidebar.
    flexGrow: 1,
    flexShrink: 1,
    flexBasis: 0,
    minWidth: 0,
  },
  mainContent: {
    gap: 16,
    paddingRight: 8,
  },
  card: {
    backgroundColor: '#fff',
    borderRadius: 12,
    padding: 20,
    ...Platform.select({
      web: {
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
      },
      default: {
        shadowColor: '#000',
        shadowOffset: {width: 0, height: 2},
        shadowOpacity: 0.1,
        shadowRadius: 8,
        elevation: 3,
      },
    }),
  },
  sidebar: {
    // DIS-1608: a fixed basis, not min/max. With only minWidth/maxWidth the
    // sidebar sized to its content, so mounting the coupon status panel or
    // swapping the button title to 'ADD MORE TO CONTINUE WITH COUPON' re-measured
    // the column and reflowed the left pane. flexGrow/flexShrink 0 pins it.
    flexBasis: '33%',
    flexGrow: 0,
    flexShrink: 0,
    alignSelf: 'flex-start',
    gap: 16,
  },
});
